<template>
  <main class="carte-publique-page">
    <section class="digital-card" v-if="carte">
      <header class="card-header">
        <div class="brand">
          <img src="../assets/logo.png" alt="FIDmarket" />
          <span>FIDmarket</span>
        </div>
        <span class="status">Active</span>
      </header>

      <div class="client-block">
        <span class="label">Carte digitale</span>
        <h1>{{ carte.prenom }}</h1>
        <p v-if="carte.commercant">{{ carte.commercant }}</p>
      </div>

      <div class="card-number">
        <span>Numero de carte</span>
        <strong>{{ carte.numero_carte }}</strong>
      </div>

      <div class="points-panel">
        <span>Points disponibles</span>
        <strong>{{ carte.points_solde }}</strong>
      </div>

      <div class="reward-panel" :class="{ available: recompenseDisponible }" v-if="carte.recompense_disponible">
        <span>{{ recompenseDisponible ? 'Recompense disponible' : 'Prochaine recompense' }}</span>
        <strong>{{ rewardLabel }}</strong>
      </div>
    </section>

    <section class="state-card" v-else-if="loading">
      <img src="../assets/logo.png" alt="FIDmarket" />
      <p>Chargement de la carte...</p>
    </section>

    <section class="state-card error" v-else>
      <img src="../assets/logo.png" alt="FIDmarket" />
      <h1>Carte introuvable</h1>
      <p>{{ erreur }}</p>
    </section>
  </main>
</template>

<script>
import api from '../api'

export default {
  name: 'CartePubliqueView',
  data() {
    return {
      carte: null,
      loading: true,
      erreur: ''
    }
  },
  computed: {
    recompenseDisponible() {
      return Boolean(this.carte?.recompense_disponible?.disponible)
    },
    rewardLabel() {
      const recompense = this.carte?.recompense_disponible
      if (!recompense) return ''
      if (this.recompenseDisponible) {
        const valeur = Number(recompense.valeur_reduction || 0)
        return valeur > 0 ? this.formatEuro(valeur) : 'A utiliser en boutique'
      }
      const seuil = Number(recompense.seuil_points || 0)
      const points = Number(this.carte?.points_solde || 0)
      return `${Math.max(seuil - points, 0)} pts restants`
    }
  },
  methods: {
    formatEuro(v) {
      return Number(v || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })
    },
    async chargerCarte() {
      this.loading = true
      this.erreur = ''
      try {
        const token = this.$route.params.token
        const res = await api.get(`/public/carte/${encodeURIComponent(token)}`)
        this.carte = res.data
      } catch (err) {
        this.erreur = err.response?.data?.error || 'Impossible de charger cette carte.'
      } finally {
        this.loading = false
      }
    }
  },
  mounted() {
    this.chargerCarte()
  }
}
</script>

<style scoped>
.carte-publique-page {
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: 22px 14px;
  background:
    radial-gradient(circle at 18% 12%, rgba(67, 176, 113, 0.22), transparent 28%),
    linear-gradient(150deg, #f7fafc 0%, #eef2f7 48%, #fdf7ed 100%);
  color: #102033;
}

.digital-card,
.state-card {
  width: min(100%, 390px);
  border: 1px solid rgba(255, 255, 255, 0.75);
  border-radius: 8px;
  background: linear-gradient(145deg, #111827 0%, #17213a 54%, #275d49 100%);
  box-shadow: 0 26px 70px rgba(15, 23, 42, 0.24);
  color: #ffffff;
}

.digital-card {
  padding: 20px;
}

.card-header,
.brand,
.card-number,
.points-panel,
.reward-panel {
  display: flex;
  align-items: center;
}

.card-header {
  justify-content: space-between;
  gap: 16px;
}

.brand {
  gap: 10px;
  min-width: 0;
  font-size: 15px;
  font-weight: 800;
}

.brand img,
.state-card img {
  width: 38px;
  height: 38px;
  object-fit: contain;
  border-radius: 8px;
  background: #ffffff;
  padding: 4px;
}

.status {
  flex: 0 0 auto;
  border: 1px solid rgba(134, 239, 172, 0.45);
  border-radius: 999px;
  padding: 7px 11px;
  background: rgba(22, 163, 74, 0.16);
  color: #bbf7d0;
  font-size: 12px;
  font-weight: 800;
}

.client-block {
  padding: 44px 0 30px;
}

.label,
.card-number span,
.points-panel span,
.reward-panel span {
  display: block;
  color: rgba(255, 255, 255, 0.68);
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}

.client-block h1 {
  margin: 8px 0 0;
  font-size: 40px;
  line-height: 1.04;
  font-weight: 900;
}

.client-block p {
  margin: 10px 0 0;
  color: rgba(255, 255, 255, 0.78);
  font-size: 15px;
}

.card-number,
.points-panel,
.reward-panel {
  justify-content: space-between;
  gap: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.14);
  padding: 18px 0;
}

.card-number strong {
  color: #facc15;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 24px;
  letter-spacing: 0;
}

.points-panel strong {
  font-size: 34px;
  font-weight: 900;
}

.reward-panel {
  margin-top: 4px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.08);
}

.reward-panel.available {
  border-color: rgba(250, 204, 21, 0.5);
  background: rgba(250, 204, 21, 0.14);
}

.reward-panel strong {
  text-align: right;
  font-size: 18px;
}

.state-card {
  display: grid;
  justify-items: center;
  gap: 14px;
  padding: 32px 22px;
  text-align: center;
}

.state-card h1 {
  margin: 0;
  font-size: 26px;
}

.state-card p {
  margin: 0;
  color: rgba(255, 255, 255, 0.78);
}

@media (max-width: 380px) {
  .digital-card {
    padding: 18px;
  }

  .client-block h1 {
    font-size: 34px;
  }

  .card-number strong {
    font-size: 21px;
  }
}
</style>
